/*
 * Tablero cross-cuenta: dónde está cada engagement en D0–D6 y qué lo detiene.
 *
 * La columna la decide la fase de la cuenta, la misma que muestran la tabla de
 * Cuentas y el Resumen: sale del expediente y, cuando ya lo rebasó, de lo
 * comercial (propuesta enviada o aceptada = D6). D0 · Radar es donde vive lo que
 * todavía no tiene expediente; la tarjeta lo dice con su estado.
 *
 * Lo que bloquea a cada cuenta sale de la cola de Hoy, pero solo lo del método:
 * un cobro vencido es urgente y no tiene nada que hacer en este tablero.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useCuentas, useHoy } from "../api/hooks";
import { FASES, type Cuenta } from "../api/tipos";
import { Abierto, Esqueleto, MarcaRelacional } from "../ui/primitivas";

/** Solo los pendientes del descubrimiento: verificación y lo que frena una fase. */
const ES_DEL_METODO = (ir: string) => ir.startsWith("/verificacion/") || ir.includes("/descubrimiento");

export function Descubrimiento() {
  const nav = useNavigate();
  const { data: cuentas, isLoading } = useCuentas();
  const { data: hoy } = useHoy();

  const bloqueo = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of hoy?.grupos ?? []) {
      for (const it of g.items) {
        if (ES_DEL_METODO(it.ir) && !m.has(it.cuenta_id)) m.set(it.cuenta_id, it.titulo);
      }
    }
    return m;
  }, [hoy]);

  const conExpediente = (c: Cuenta) => c.casos.some((k) => k.estado !== "F2");

  return (
    <div className="pagina" style={{ maxWidth: 1400 }}>
      <header style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <h1 className="titulo">Descubrimiento</h1>
        <span style={{ fontSize: 12.5, color: "var(--faint)" }}>Las compuertas son del método, no del tablero: se avanza desde la cuenta.</span>
      </header>
      {isLoading && <div className="tarjeta"><Esqueleto lineas={4} /></div>}
      {cuentas && (
        <div style={{ overflowX: "auto", paddingBottom: 8 }}>
          <div style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: 236, gap: 12, alignItems: "start" }}>
            {FASES.map((f, fi) => {
              const col = cuentas.filter((c) => c.fase === fi).sort((a, b) => b.fit - a.fit);
              return (
                <div key={f.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "0 4px 8px" }}>
                    <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}><span className="mono" style={{ color: "var(--sky)", marginRight: 6 }}>{f.id}</span>{f.nombre}</span>
                    <span className="mono" style={{ color: "var(--faint)" }}>{col.length}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 96, maxHeight: "calc(100vh - 220px)", overflowY: "auto", padding: 6, background: "var(--sunken)", borderRadius: "var(--r-card)" }}>
                    {col.map((c) => {
                      // Dos líneas, como el prototipo: quién, cuánto lleva de esta fase,
                      // y qué la detiene. La etapa comercial se lee en Cuentas; aquí lo
                      // que importa es el avance dentro del método.
                      const b = bloqueo.get(c.id) ?? (conExpediente(c) ? null : c.proxima_accion);
                      return (
                        <button key={c.id} onClick={() => nav(`/cuentas/${c.id}/descubrimiento`)} className="tarjeta-tablero"
                          style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--r-control)", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                          <span style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "baseline", gap: 8 }}>
                            <span style={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.empresa}<MarcaRelacional show={c.relacional} /></span>
                            <span className="mono" style={{ color: "var(--faint)", fontSize: 11, flexShrink: 0 }}
                              title={c.requisitos ? "Requisitos del método cubiertos para pasar de fase" : "Sin expediente abierto"}>
                              {c.requisitos ? `${c.requisitos.cumplidos}/${c.requisitos.total}` : "—"}
                            </span>
                          </span>
                          {b && <Abierto style={{ padding: "5px 8px", fontSize: 11.5, lineHeight: 1.4, width: "100%", borderRadius: "var(--r-control)", textAlign: "left" }}>{b}</Abierto>}
                        </button>
                      );
                    })}
                    {col.length === 0 && <div className="abierto" style={{ height: 40, borderRadius: "var(--r-control)", opacity: .6 }} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
